/**
 * TemplateSuggestor Component
 * Phase 5: Session Management UI (2026-01-19)
 *
 * Suggests appropriate WhatsApp templates based on session state
 * and conversation context. Shows session awareness indicators.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button as MuiButton,
  IconButton,
  Tooltip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  AccessTime,
  Warning,
  CheckCircle,
  Send,
  Timer,
  Message,
  Schedule,
} from '@mui/icons-material';
import { ChevronDown, RefreshCw, FileText } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { cn } from '../../lib/utils';
import api from '../../services/api';

interface WhatsAppTemplate {
  id: number;
  name: string;
  category: string;
  language_code: string;
  components: {
    header?: any;
    body?: string;
    footer?: string;
    buttons?: any[];
  };
  variables: string[];
  approval_status: string;
  quality_score?: string;
}

interface SessionState {
  state: 'ACTIVE_24H' | 'TEMPLATE_ONLY' | 'NO_RESPONSE' | 'CLOSED';
  expires_at?: string;
  last_user_message_at?: string;
  hours_remaining?: number;
}

interface TemplateSuggestorProps {
  conversationId: number;
  contactPhone?: string;
  contact?: { id: number; name?: string; email?: string; phone_number?: string; custom_attributes?: Record<string, unknown>; city?: string; country?: string };
  onSelectTemplate: (template: WhatsAppTemplate, variables: Record<string, string>) => void;
  compact?: boolean;
  /** When this value changes, session state and template list are re-fetched (e.g. after sending a message). */
  refreshTrigger?: number;
}

const TemplateSuggestor: React.FC<TemplateSuggestorProps> = ({
  conversationId,
  contactPhone,
  contact,
  onSelectTemplate,
  compact = false,
  refreshTrigger
}) => {
  const [sessionState, setSessionState] = useState<SessionState | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [suggestedTemplates, setSuggestedTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);

  // Filter suggested templates based on session state (used by fetchData)
  const filterSuggestedTemplatesRef = (
    allTemplates: WhatsAppTemplate[],
    session: SessionState | null
  ): WhatsAppTemplate[] => {
    if (!session) return allTemplates.slice(0, 3);
    if (session.state === 'ACTIVE_24H') {
      return allTemplates.filter(t => t.category === 'UTILITY').slice(0, 3);
    }
    if (session.state === 'TEMPLATE_ONLY') {
      const marketing = allTemplates.filter(t => t.category === 'MARKETING');
      const utility = allTemplates.filter(t => t.category === 'UTILITY');
      return [...marketing, ...utility].slice(0, 5);
    }
    return allTemplates.slice(0, 3);
  };

  const fetchData = React.useCallback(async () => {
    if (!conversationId) return;
    try {
      setLoading(true);
      const cacheBust = { params: { _: Date.now() } };
      const sessionResponse = await api.get(`/whatsapp/sessions/${conversationId}`, cacheBust);
      setSessionState(sessionResponse.data);
      // Cache-bust so browser/proxy never return stale template list (e.g. after adding/syncing templates)
      const templatesResponse = await api.get('/campaigns/whatsapp-templates/', cacheBust);
      const allTemplates = templatesResponse.data.templates || [];
      setTemplates(allTemplates);
      const suggested = filterSuggestedTemplatesRef(allTemplates, sessionResponse.data);
      setSuggestedTemplates(suggested);
    } catch (error) {
      console.error('Error fetching template data:', error);
      setSessionState({ state: 'TEMPLATE_ONLY' });
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  // Fetch session state and templates when conversation or refreshTrigger changes
  useEffect(() => {
    if (conversationId) {
      fetchData();
    }
  }, [conversationId, refreshTrigger, fetchData]);

  // Get session state indicator
  const getSessionIndicator = () => {
    if (!sessionState) return null;

    switch (sessionState.state) {
      case 'ACTIVE_24H':
        return {
          icon: <CheckCircle color="success" />,
          label: 'Active Session',
          color: 'success' as const,
          description: `Session active - ${sessionState.hours_remaining || 0}h remaining`
        };
      case 'TEMPLATE_ONLY':
        return {
          icon: <Warning color="warning" />,
          label: 'Template Only',
          color: 'warning' as const,
          description: 'Outside 24h window - Templates required'
        };
      case 'NO_RESPONSE':
        return {
          icon: <Timer color="info" />,
          label: 'Awaiting Response',
          color: 'info' as const,
          description: 'Waiting for customer reply'
        };
      case 'CLOSED':
        return {
          icon: <AccessTime color="error" />,
          label: 'Session Closed',
          color: 'error' as const,
          description: 'Session expired - Use template to re-engage'
        };
      default:
        return null;
    }
  };

  // const handleSelectTemplate = (template: WhatsAppTemplate) => {
  //   setSelectedTemplate(template);
  //   // Create default variable mappings
  //   const variables: Record<string, string> = {};
  //   //template.variables.forEach((v, index) => {
  //   //  variables[`${index + 1}`] = `{{${v}}}`;
  //   //});
  //   if (template.variables && template.variables.length > 0) {
  //     template.variables.forEach((v, index) => {
  //       variables[`${index + 1}`] = ''; // Empty string, not placeholder
  //     });
  //   }
  //   onSelectTemplate(template, variables);
  // };

  // Added on 09/02/2026 by NK for Dynamic Variables and Media Templates Code Implementation
  const handleSelectTemplate = (template: WhatsAppTemplate) => {
    const variables: Record<string, string> = {};
    if (template.variables && template.variables.length > 0) {
      template.variables.forEach((v, index) => {
        const key = `${index + 1}`;
        if (contact) {
          if (v === 'contact.name' || v === 'name') variables[key] = contact.name ?? '';
          else if (v === 'contact.first_name') variables[key] = (contact.name ?? '').split(/\s+/)[0] ?? '';
          else if (v === 'contact.last_name') variables[key] = (contact.name ?? '').split(/\s+/).slice(1).join(' ') ?? '';
          else if (v === 'contact.email' || v === 'email') variables[key] = contact.email ?? '';
          else if (v === 'contact.phone_number' || v === 'phone_number') variables[key] = contact.phone_number ?? '';
          else if (v === 'contact.company') variables[key] = (contact.custom_attributes as Record<string, string> | undefined)?.company ?? '';
          else if (v === 'contact.city') variables[key] = contact.city ?? '';
          else if (v === 'contact.country') variables[key] = contact.country ?? '';
          else variables[key] = '';
        } else {
          variables[key] = '';
        }
      });
    }
    setSelectedTemplate(template);
    onSelectTemplate(template, variables);
  };
  // End of Added on 09/02/2026 by NK for Dynamic Variables and Media Templates Code Implementation


  const sessionIndicator = getSessionIndicator();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (compact) {
    const sessionDot = sessionIndicator
      ? sessionIndicator.color === 'success' ? 'bg-emerald-500'
        : sessionIndicator.color === 'warning' ? 'bg-amber-500'
        : sessionIndicator.color === 'error' ? 'bg-red-500'
        : 'bg-blue-500'
      : null;

    return (
      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs font-normal border-border/60 bg-background hover:bg-accent"
            >
              {sessionDot && (
                <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", sessionDot)} />
              )}
              <FileText className="h-3.5 w-3.5" />
              Templates
              <span className="text-muted-foreground">({templates.length})</span>
              <ChevronDown className="h-3 w-3 ml-0.5 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="top"
            sideOffset={6}
            className="w-72"
          >
            <DropdownMenuLabel className="flex items-center justify-between py-1.5">
              <span className="text-xs font-medium">WhatsApp Templates</span>
              {sessionIndicator && (
                <span className={cn(
                  "text-[10px] px-1.5 py-0.5 rounded-full font-normal",
                  sessionIndicator.color === 'success' ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
                  : sessionIndicator.color === 'warning' ? "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400"
                  : sessionIndicator.color === 'error' ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400"
                  : "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400"
                )}>
                  {sessionIndicator.label}
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <ScrollArea className="max-h-[280px]">
              {templates.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  No templates available
                </p>
              ) : (
                templates.map(template => (
                  <DropdownMenuItem
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className={cn(
                      "flex items-center justify-between gap-3 cursor-pointer",
                      selectedTemplate?.id === template.id && "bg-accent"
                    )}
                  >
                    <span className="text-xs truncate flex-1">{template.name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0 uppercase tracking-wide">
                      {template.category}
                    </span>
                  </DropdownMenuItem>
                ))
              )}
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Refresh */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); fetchData(); }}
          disabled={loading}
          title="Refresh templates"
        >
          {loading
            ? <CircularProgress size={12} />
            : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      {/* Session State Banner */}
      {sessionIndicator && (
        <Alert
          severity={sessionIndicator.color}
          icon={sessionIndicator.icon}
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2">{sessionIndicator.label}</Typography>
          <Typography variant="caption">{sessionIndicator.description}</Typography>
        </Alert>
      )}

      {/* Template Suggestions Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Suggested Templates
        </Typography>
        <Badge badgeContent={suggestedTemplates.length} color="primary">
          <Message color="action" />
        </Badge>
      </Box>

      {/* Template List */}
      {suggestedTemplates.length > 0 ? (
        <List dense>
          {suggestedTemplates.map((template, index) => (
            <React.Fragment key={template.id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                button
                selected={selectedTemplate?.id === template.id}
                onClick={() => handleSelectTemplate(template)}
              >
                <ListItemText
                  primary={template.name}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip
                        label={template.category}
                        size="small"
                        sx={{ fontSize: '0.7rem' }}
                      />
                      <Chip
                        label={template.language_code}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Use this template">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectTemplate(template);
                      }}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No templates available. Create templates in the Campaign settings.
        </Typography>
      )}

      {/* Session Timer */}
      {sessionState?.expires_at && sessionState.state === 'ACTIVE_24H' && (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Session expires: {new Date(sessionState.expires_at).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default TemplateSuggestor;
